import React, { useMemo, Fragment, useState } from 'react';
import {
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Colxx } from '../../../../../../../components/common/CustomBootstrap';
import ReactTable from 'react-table';
import WorkOrderModal, { getChannelIntegration } from '../WorkOrderModal';
import { NotificationManager } from '../../../../../../../components/common/react-notifications';
import { AxiosAPI } from '../../../../../../../helpers/AxiosAPI';
import {
  API_Partner_Flexkeeping,
  API_WorkOrders,
} from '../../../../../../../constants/APIs';
import moment from 'moment';

const formatDate = (value) => {
  if (!value) return '-';
  const mDate = moment(value);
  if (!mDate.isValid()) return '-';
  return mDate.format('MMM D, YYYY h:mm A');
};

const WORK_ORDER_TYPE_OPTIONS = [
  { title: 'Task', value: 'task' },
  { title: 'Repair', value: 'repair' },
  { title: 'Room order', value: 'room_order' },
  { title: 'Flaw cleaning', value: 'flaw_cleaning' },
  { title: 'Issue', value: 'issue' },
  { title: 'Feedback', value: 'feedback' },
];

const ReservationPagination = ({
  page,
  totalPages,
  onPageChange,
  pagesPerChunk,
}) => {
  const chunkSize = Math.max(1, pagesPerChunk || 1);
  const chunkStart = Math.floor(page / chunkSize) * chunkSize;
  const chunkEnd = Math.min(chunkStart + chunkSize, totalPages);
  const canPrevious = page > 0;
  const canNext = totalPages ? page < totalPages - 1 : false;

  const renderPageButtons = () => {
    const items = [];
    for (let i = chunkStart; i < chunkEnd; i += 1) {
      items.push(
        <PaginationItem key={i} active={i === page}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(i);
            }}
          >
            {i + 1}
          </PaginationLink>
        </PaginationItem>
      );
    }
    return items;
  };

  return (
    <div className="d-flex flex-column align-items-center mt-3">
      <Pagination size="sm" className="mb-2">
        <PaginationItem disabled={!canPrevious}>
          <PaginationLink
            href="#"
            onClick={() => {
              if (canPrevious) onPageChange(page - 1);
            }}
          >
            <i className="simple-icon-arrow-left" />
          </PaginationLink>
        </PaginationItem>
        {renderPageButtons()}
        <PaginationItem disabled={!canNext}>
          <PaginationLink
            href="#"
            onClick={() => {
              if (canNext) onPageChange(page + 1);
            }}
          >
            <i className="simple-icon-arrow-right" />
          </PaginationLink>
        </PaginationItem>
      </Pagination>
      <span className="text-muted text-small">
        Page {totalPages ? Math.min(page + 1, totalPages) : 0} of{' '}
        {totalPages || 0}
      </span>
    </div>
  );
};

export default function WorkOrdersList({
  data = [],
  loading,
  onRefresh,
  page = 0,
  pageSize = 10,
  totalPages = 0,
  totalItems = 0,
  pagesPerChunk = 5,
  onPageChange = () => {},
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [editItem, setEditItem] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const confirmDelete = (item) => {
    setDeleteItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    const item = deleteItem;
    try {
      await AxiosAPI({
        method: 'delete',
        url: `${API_WorkOrders}/${item?.id}`,
      });

      if (item?.system == 'Flexkeeping') {
        const response = await getChannelIntegration(
          item?.groupId,
          'Flexkeeping'
        );
        const [integration] = response?.data;
        const res = await AxiosAPI({
          method: 'post',
          url: `${API_Partner_Flexkeeping}/cancel-assignment`,
          headers: {
            'Content-Type': 'application/json',
            'api-Key': integration?.flexkeepingIntegration?.apiKey,
            'User-Agent': 'integrator-a',
          },
          data: {
            id_facility: item?.facilityId,
            id_assignment: item?.externalWorkOrderId,
          },
        });
      }

      NotificationManager.success(
        'Work order deleted successfully',
        'Success',
        3000
      );
      if (onRefresh) onRefresh();
    } catch (error) {
      NotificationManager.error('Failed to delete work order', 'Error', 3000);
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteItem(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Work Order Type',
        accessor: 'workOrderType',
        Cell: (props) => {
          const matchedType = WORK_ORDER_TYPE_OPTIONS.find(
            (opt) => opt.value === props.value
          );
          return (
            <p className="mb-0 font-weight-bold">
              {matchedType ? matchedType.title : props.value || '-'}
            </p>
          );
        },
        style: { whiteSpace: 'unset' },
      },
      {
        Header: 'Message',
        accessor: 'message',
        Cell: (props) => (
          <p className="mb-0 text-muted">{props.value || '-'}</p>
        ),
        style: { whiteSpace: 'unset' },
      },
      {
        Header: 'Room / Facility',
        id: 'roomFacility',
        Cell: (props) => {
          const room = props.original.roomTitle;
          const facility = props.original.facilityId;
          return (
            <p className="mb-0">
              {room ? `${room} ` : ''}
              {facility ? `(${facility})` : ''}
            </p>
          );
        },
        style: { whiteSpace: 'unset' },
      },
      {
        Header: 'Priority',
        accessor: 'priority',
        Cell: (props) => {
          let color = 'primary';
          if (props.value === 3) color = 'warning';
          if (props.value >= 4) color = 'danger';
          return <span className={`badge badge-${color}`}>{props.value}</span>;
        },
        width: 150,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (props) => {
          let color = 'secondary';
          const val = (props.value || '').toLowerCase();
          if (val === 'completed') color = 'success';
          else if (val === 'in progress' || val === 'acknowledged')
            color = 'info';
          else if (val === 'cancelled') color = 'danger';
          return (
            <span className={`badge badge-outline-${color}`}>
              {props.value || '-'}
            </span>
          );
        },
        width: 140,
      },
      {
        Header: 'Execution Date',
        accessor: 'executionUtc',
        Cell: (props) => <p className="mb-0">{formatDate(props.value)}</p>,
        style: { whiteSpace: 'unset' },
        width: 180,
      },
      {
        Header: 'Actions',
        id: 'actions',
        width: 160,
        Cell: (props) => {
          return (
            <div
              className="d-flex align-items-center justify-content-start"
              style={{ gap: '8px' }}
            >
              <Button
                outline
                size="xs"
                color="primary"
                className="px-3"
                onClick={() => {
                  setEditItem(props.original);
                  setIsEditModalOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                outline
                size="xs"
                color="danger"
                className="px-3"
                onClick={() => confirmDelete(props.original)}
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [onRefresh]
  );

  if (loading) {
    return (
      <Card className="d-flex flex-row mb-3">
        <CardBody>
          <h4 style={{ textAlign: 'center' }}>Loading...</h4>
        </CardBody>
      </Card>
    );
  }

  return (
    <Fragment>
      <Colxx xxs="12" className="mb-2">
        <Card className="mb-2">
          <CardBody>
            <Fragment>
              <ReactTable
                data={rows}
                columns={columns}
                defaultPageSize={pageSize}
                showPagination={false}
                filterable={false}
                className="-striped -highlight -bordered"
                minRows={rows.length ? undefined : pageSize}
                noDataText="No work orders found"
              />
              {totalPages > 1 && (
                <ReservationPagination
                  page={page}
                  totalPages={totalPages}
                  pagesPerChunk={pagesPerChunk}
                  onPageChange={onPageChange}
                />
              )}
              {totalPages > 1 && (
                <div className="text-muted text-right mt-2">
                  Showing page {totalPages ? page + 1 : 0} of {totalPages || 0}{' '}
                  ({totalItems} total)
                </div>
              )}
            </Fragment>
          </CardBody>
        </Card>
      </Colxx>

      {isEditModalOpen && (
        <WorkOrderModal
          visible={isEditModalOpen}
          toggle={() => {
            setIsEditModalOpen(false);
            setEditItem(null);
          }}
          onSuccess={onRefresh}
          item={editItem}
        />
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        toggle={() => setIsDeleteModalOpen(false)}
      >
        <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this work order?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" size="xs" onClick={handleDelete}>
            Delete
          </Button>{' '}
          <Button color="secondary" size="xs" outline onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
}
